import { useCallback, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronsLeft, ChevronsRight, LogOut, Menu, RotateCw, Search, Shield, UserCircle, X } from 'lucide-react';
import { logout } from '@/lib/api/admin';
import type { Me } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { useHotkey } from '@/lib/hooks';
import { IconButton } from '@/components/ui/button';
import { Menu as Popover, MenuItem, MenuSeparator } from '@/components/ui/menu';
import { Kbd } from '@/components/ui/page';
import { CommandPalette } from './command-palette';
import { NAV } from './nav';
import { ThemeToggle } from './theme-toggle';

const COLLAPSE_KEY = 'da.sidebar';

/**
 * The frame every signed-in screen sits in: a dark rail on the left with the
 * sections, collapsible to icons; a slim top bar with search (⌘K), a refresh
 * for every query on screen, the theme and the account. On a phone the rail
 * becomes a drawer behind the top bar.
 */
export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; } });
  const [palette, setPalette] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetching = useIsFetching();

  const toggleCollapsed = () => setCollapsed((c) => { try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch { /* ignore */ } return !c; });

  useHotkey('mod+k', useCallback((e: KeyboardEvent) => { e.preventDefault(); setPalette((p) => !p); }, []));

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => { qc.clear(); navigate('/login', { replace: true }); },
  });

  const rail = (mobile: boolean) => {
    const narrow = collapsed && !mobile;
    return (
      <div className="flex h-full flex-col bg-sidebar text-sidebar-tx">
        <div className={cn('flex h-14 items-center gap-2.5 border-b border-sidebar-bd', narrow ? 'justify-center px-2' : 'px-4')}>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-white"><Shield size={16} /></span>
          {!narrow && (
            <>
              <span className="text-sm font-bold text-white">Dravizo</span>
              <span className="rounded-md bg-sidebar-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Admin</span>
            </>
          )}
          {mobile && (
            <button type="button" onClick={() => setDrawer(false)} aria-label="Закрыть меню" className="ml-auto rounded-lg p-1 hover:bg-sidebar-2 hover:text-white"><X size={18} /></button>
          )}
        </div>

        <nav className="scroll-thin flex-1 overflow-y-auto px-2 py-3" aria-label="Разделы">
          {NAV.map((section) => (
            <div key={section.title} className="mb-3">
              {!narrow && <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-tx/60">{section.title}</p>}
              {section.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  title={narrow ? label : undefined}
                  onClick={() => setDrawer(false)}
                  className={({ isActive }) => cn(
                    'my-0.5 flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
                    narrow ? 'justify-center px-0 py-2.5' : 'px-3 py-2',
                    isActive ? 'bg-accent text-white shadow-sm' : 'hover:bg-sidebar-2 hover:text-white',
                  )}
                >
                  <Icon size={17} className="shrink-0" />
                  {!narrow && <span className="truncate">{label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {!mobile && (
          <button type="button" onClick={toggleCollapsed} title={collapsed ? 'Развернуть меню' : 'Свернуть меню'} className="flex h-10 items-center justify-center border-t border-sidebar-bd text-sidebar-tx hover:bg-sidebar-2 hover:text-white">
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen">
      <aside className={cn('sticky top-0 hidden h-screen shrink-0 transition-[width] duration-200 md:block', collapsed ? 'w-16' : 'w-60')}>{rail(false)}</aside>

      {drawer && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] shadow-2xl animate-fade-in">{rail(true)}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-surface/85 px-3 backdrop-blur md:px-6">
          <IconButton title="Открыть меню" className="md:hidden" onClick={() => setDrawer(true)}><Menu size={20} /></IconButton>

          <button
            type="button"
            onClick={() => setPalette(true)}
            className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-line bg-surface-2/60 px-3 text-sm text-muted transition-colors hover:border-line-strong hover:text-ink md:max-w-md"
          >
            <Search size={15} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left">Найти отель, пользователя, тариф…</span>
            <span className="hidden items-center gap-1 sm:flex"><Kbd>Ctrl</Kbd><Kbd>K</Kbd></span>
          </button>

          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <IconButton title="Обновить данные" onClick={() => qc.invalidateQueries()}>
              <RotateCw size={16} className={fetching > 0 ? 'animate-spin text-accent' : undefined} />
            </IconButton>
            <span className="hidden sm:inline-flex"><ThemeToggle compact /></span>
            <Popover trigger={({ toggle }) => (
              <button type="button" onClick={toggle} title={me.email} className="ml-1 flex h-9 items-center gap-2 rounded-lg pl-1 pr-2 hover:bg-surface-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-accent-ink">{initials(me.displayName || me.email)}</span>
                <span className="hidden max-w-[140px] truncate text-sm font-medium text-ink lg:inline">{me.displayName || me.email}</span>
              </button>
            )}>
              <div className="px-2.5 py-2">
                <p className="truncate text-sm font-semibold text-ink">{me.displayName || me.email}</p>
                <p className="truncate text-xs text-muted">{me.email}</p>
              </div>
              <MenuSeparator />
              <MenuItem icon={<UserCircle size={15} />} onClick={() => navigate('/account')}>Профиль и сессии</MenuItem>
              <MenuSeparator />
              <MenuItem icon={<LogOut size={15} />} danger disabled={signOut.isPending} onClick={() => signOut.mutate()}>Выйти</MenuItem>
            </Popover>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-8">{children}</main>
      </div>

      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </div>
  );
}
